import { isWebAlert } from '@m2h/common/constants';
import { getLanguage, getRegion } from '@m2h/common/utils';
import { isAlertIframeOpen } from '@m2h/common/utils/iframe';
import { initRegionConfigs } from '@m2h/common/utils/location';
import React, { useEffect } from 'react';

const HOME_OWNED_PATHS = new Set([
  '*',
  '/',
  '/mpweb',
  '/mpweb/',
  '/mpweb/home_page_main',
  '/mpweb/home_page_main/',
]);
const LANGUAGE_POPUP_SCHEME = 'sailorc://keeta.com/machproalert?bundle_name='
  + 'mach_pro_sailor_c_address_change_region_popup&level=0';

let openingSignature = '';
let openingSignatureTimer: ReturnType<typeof setTimeout> | undefined;

export const normalizeLocaleTag = (locale: unknown): string => {
  if (typeof locale !== 'string') return '';
  const trimmedLocale = locale.trim();
  if (!trimmedLocale) return '';
  const quote = trimmedLocale[0];
  const unquotedLocale = (
    trimmedLocale.length >= 2
    && (quote === '"' || quote === "'")
    && trimmedLocale[trimmedLocale.length - 1] === quote
  )
    ? trimmedLocale.slice(1, -1).trim()
    : trimmedLocale;
  return unquotedLocale.replace(/_/g, '-').toLowerCase();
};

export const isLocaleSupported = (locale: unknown, supportLocales: unknown): boolean => {
  const normalizedLocale = normalizeLocaleTag(locale);
  if (!normalizedLocale || !Array.isArray(supportLocales)) return false;
  return supportLocales.some((item) => {
    const normalizedItem = normalizeLocaleTag(item?.locale ?? item);
    return normalizedItem !== '' && normalizedItem === normalizedLocale;
  });
};

export const isHomeOwnedRoute = (routePath: string, pathname: string): boolean =>
  HOME_OWNED_PATHS.has(routePath) || HOME_OWNED_PATHS.has(pathname);

interface RegionLocaleMismatchGuardProps {
  routePath: string;
}

const RegionLocaleMismatchGuard = ({ routePath }: RegionLocaleMismatchGuardProps) => {
  useEffect(() => {
    if (isWebAlert || isHomeOwnedRoute(routePath, window.location.pathname)) return undefined;

    let cancelled = false;
    const checkMismatch = async () => {
      await initRegionConfigs();
      if (cancelled || isAlertIframeOpen()) return;

      const SLCompass = Mach.requireModule('SLCompass');
      const SLMPI18N = Mach.requireModule('Intl');
      const SLLocation = Mach.requireModule('SLLocation');
      const region = SLCompass?.getCompassInfo?.()?.region || getRegion();
      const locale = SLMPI18N?.getCurrentLocaleInfo?.()?.locale || getLanguage();
      const supportLocales = SLMPI18N?.allTextLocales?.({ region });
      if (!region || isLocaleSupported(locale, supportLocales)) return;

      const { longitude, latitude } = SLLocation?.getLocation?.() || {};
      const normalizedLongitude = Number(longitude);
      const normalizedLatitude = Number(latitude);
      if (!Number.isFinite(normalizedLongitude) || !Number.isFinite(normalizedLatitude)) return;

      const signature = `${String(region).trim().toUpperCase()}|${normalizeLocaleTag(locale)}`;
      if (!signature || openingSignature === signature || isAlertIframeOpen()) return;
      openingSignature = signature;
      clearTimeout(openingSignatureTimer);
      openingSignatureTimer = setTimeout(() => {
        if (openingSignature === signature) openingSignature = '';
      }, 1000);

      const data = encodeURIComponent(JSON.stringify({
        selectedCity: {
          region,
          location: `${normalizedLongitude},${normalizedLatitude}`,
        },
        popupLanguageScene: 1,
        h5GlobalLanguageMismatch: true,
      }));
      Mach.requireModule('Router')?.navigateTo(`${LANGUAGE_POPUP_SCHEME}&data=${data}`);
    };

    checkMismatch().catch((error) => {
      console.warn('[RegionLocaleMismatchGuard] check failed', error);
    });
    return () => {
      cancelled = true;
    };
  }, [routePath]);

  return null;
};

export default RegionLocaleMismatchGuard;
